import { useCallback, useEffect, useRef, useState } from 'react';
import { marketingViewsApi, type MarketingView, type MarketingViewScope, type MarketingSnapshot } from '../api/marketingViews';
import { portalViewStorageKey, readPortalViews } from '../lib/channelPortalViews';

export function useMarketingViews(scope: MarketingViewScope, active: boolean) {
  const key = JSON.stringify(scope);
  const latestKey = useRef(key);
  latestKey.current = key;
  const [state, setState] = useState({ key: '', views: [] as MarketingView[], loading: false, error: '' });
  const [saving, setSaving] = useState(false);
  const [migrationWarning, setMigrationWarning] = useState('');
  const generation = useRef(0);
  const load = useCallback(async () => {
    if (!scope.brand_id || !scope.site_id) return;
    const request = ++generation.current;
    setMigrationWarning('');
    setState(current => ({ ...current, key, views: current.key === key ? current.views : [], loading: true, error: '' }));
    try {
      let result = await marketingViewsApi.list(scope);
      // One-time migration of the existing portal prototype. Remove the local copy only after a successful database import.
      if (scope.page === 'site-data/channel-portal') {
        let storageKey: string | null = null;
        let imported: MarketingView[] = [];
        try {
          const user = JSON.parse(localStorage.getItem('user_info') ?? 'null');
          if (typeof user?.id === 'string') {
            storageKey = portalViewStorageKey(user.id, scope.brand_id, scope.site_id);
            imported = readPortalViews(localStorage.getItem(storageKey)).map(view => ({ ...view, updatedAt: '', snapshot: { version: 1, sections: { portal: view.snapshot } } }));
          }
        } catch { /* A damaged browser prototype must not prevent loading database views. */ }
        if (imported.length) {
          try {
            result = await marketingViewsApi.import(scope, imported);
            try { if (storageKey) localStorage.removeItem(storageKey); } catch { /* Import is idempotent on retry. */ }
          } catch (error) {
            if (request === generation.current && latestKey.current === key) setMigrationWarning('浏览器视图尚未迁移，原数据已保留。' + (error instanceof Error ? error.message : '请稍后重试'));
          }
        }
      }
      if (request === generation.current && latestKey.current === key) setState({ key, views: result.list, loading: false, error: '' });
    } catch (error) {
      if (request === generation.current && latestKey.current === key) setState(current => ({ ...current, loading: false, error: error instanceof Error ? error.message : '快捷视图加载失败' }));
    }
  }, [key]); // Scope values are represented by key.
  useEffect(() => {
    if (!active) return;
    void load();
    return () => { generation.current++; };
  }, [active, load]);
  const mutate = async (operation: () => Promise<{ list: MarketingView[] }>) => {
    if (saving || state.loading || state.key !== key) throw new Error('快捷视图尚未就绪');
    setSaving(true);
    generation.current++;
    try {
      const result = await operation();
      if (latestKey.current === key) setState({ key, views: result.list, loading: false, error: '' });
      return result.list;
    } catch (error) { void load(); throw error; }
    finally { setSaving(false); }
  };
  return {
    views: state.key === key ? state.views : [],
    loading: state.key !== key || state.loading, saving, migrationWarning, error: state.key === key ? state.error : '', reload: load,
    save: (name: string, snapshot: MarketingSnapshot, view?: MarketingView) => mutate(() => marketingViewsApi.save(scope, { name, snapshot }, view)),
    remove: (view: MarketingView) => mutate(() => marketingViewsApi.remove(scope, view)),
  };
}
